import { atom } from "jotai";
import { apiService } from "@/api";
import type { CurrentIrrigationPlanState } from "@/api/model/current-irrigation-plan-state";
import type { IrrigationPlanState } from "@/api/model/irrigation-plan-state";
import type { AUTIrrigationPlan } from "@/api/queries/account";
import { dayjs } from "@/utils/date";
import { TimeoutTimer } from "@/utils/timer";
import { withOperationHandlingAtom } from "../operations";
import { DATA_FETCH_INTERVAL_MILLIS } from "./constants";
import {
  irrigationPlanByIdAtom,
  irrigationPlansAtom,
  projectByIdAtom,
  projectsAtom,
  sectorsByProjectIdAtom,
  selectedPropertyIdAtom,
} from "./property-domain";

// -----------------------------------------------------------------------------
// Current Irrigation Plan State atoms
// -----------------------------------------------------------------------------

export const currentIrrigationPlanStatesAtom = atom<CurrentIrrigationPlanState<{ irrigation_plan: string }>[]>([]);
currentIrrigationPlanStatesAtom.debugLabel = "currentIrrigationPlanStatesAtom";

export const currentIrrigationPlanStatesLoadingAtom = atom<boolean>(false);
currentIrrigationPlanStatesLoadingAtom.debugLabel = "currentIrrigationPlanStatesLoadingAtom";
export const currentIrrigationPlanStatesErrorAtom = atom<string | null>(null);
currentIrrigationPlanStatesErrorAtom.debugLabel = "currentIrrigationPlanStatesErrorAtom";

export const currentIrrigationPlanStatesByPropertyAtom = atom((get) => {
  const selectedPropertyId = get(selectedPropertyIdAtom);
  const states = get(currentIrrigationPlanStatesAtom);
  if (!selectedPropertyId) return [] as CurrentIrrigationPlanState[];
  return states as unknown as CurrentIrrigationPlanState[];
});
currentIrrigationPlanStatesByPropertyAtom.debugLabel = "currentIrrigationPlanStatesByPropertyAtom";

export const currentIrrigationPlanStateByPlanIdAtom = atom(
  (get) =>
    (irrigationPlanId: string): CurrentIrrigationPlanState<{ irrigation_plan: string }> | null => {
      const states = get(currentIrrigationPlanStatesAtom);
      return states.find((s) => (s.irrigation_plan as unknown as string) === irrigationPlanId) || null;
    }
);
currentIrrigationPlanStateByPlanIdAtom.debugLabel = "currentIrrigationPlanStateByPlanIdAtom";

export const fetchCurrentIrrigationPlanStatesAtom = atom(null, async (get, set) => {
  const selectedPropertyId = get(selectedPropertyIdAtom);
  if (!selectedPropertyId) {
    console.log("fetchCurrentIrrigationPlanStatesAtom - No property ID selected");
    return;
  }

  await set(
    withOperationHandlingAtom,
    {
      operation: "fetchCurrentIrrigationPlanStates",
      message: "Carregando estado atual dos planos de irrigação...",
      displayLoading: false,
      displayError: true,
    },
    async () => {
      set(currentIrrigationPlanStatesLoadingAtom, true);
      set(currentIrrigationPlanStatesErrorAtom, null);
      try {
        const states = await apiService.currentIrrigationPlanState.query({
          fields: ["*"],
          filter: {
            irrigation_plan: {
              project: { property: { _eq: selectedPropertyId } },
            },
          } as any,
          sort: ["-packet_date"],
        });
        set(currentIrrigationPlanStatesAtom, states as any);
        return states;
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        set(currentIrrigationPlanStatesErrorAtom, msg);
        console.error("Failed to fetch current irrigation plan states:", error);
        throw error;
      } finally {
        set(currentIrrigationPlanStatesLoadingAtom, false);
      }
    }
  );
});
fetchCurrentIrrigationPlanStatesAtom.debugLabel = "fetchCurrentIrrigationPlanStatesAtom";
fetchCurrentIrrigationPlanStatesAtom.onMount = (dispatch) => {
  const timer = new TimeoutTimer();
  setTimeout(() => {
    dispatch();
  }, 1100);
  timer.start(DATA_FETCH_INTERVAL_MILLIS, async () => {
    await dispatch();
  });
  return () => timer.stop();
};

// -----------------------------------------------------------------------------
// Irrigation plan state history atoms
// -----------------------------------------------------------------------------

export const IRRIGATION_PLAN_HISTORY_PAGE_SIZE = 25;

export type IrrigationPlanHistoryScope = "plan" | "project";

export interface IrrigationPlanHistoryFilters {
  scope: IrrigationPlanHistoryScope;
  projectId: string | null;
  planId: string | null;
  startDate: string;
  endDate: string;
}

export interface IrrigationPlanHistoryPagination {
  limit: number;
  offset: number;
}

export interface IrrigationPlanHistoryStepInfo {
  id: string;
  order: number | null;
  description: string | null;
  sectorId: string | null;
  sectorName: string | null;
}

export interface IrrigationPlanHistoryTimelineEntry {
  id: number;
  irrigationPlanId: string;
  planName: string | null;
  projectId: string | null;
  projectName: string | null;
  startTime: string;
  endTime: string | null;
  endTimeInferred: boolean;
  durationMs: number;
  durationSeconds: number;
  activatedSteps: IrrigationPlanHistoryStepInfo[];
  activatedFertiSteps: IrrigationPlanHistoryStepInfo[];
  usesWaterPump: boolean;
  usesFerti: boolean;
  waterpumpWorking: boolean;
  packetDate: string;
  isOngoing: boolean;
}

export interface IrrigationPlanHistorySummary {
  totalEntries: number;
  ongoingEntries: number;
  usesWaterPumpCount: number;
  usesFertiCount: number;
  lastEntry: IrrigationPlanHistoryTimelineEntry | null;
}

const toIsoString = (value: string | Date | null | undefined) => {
  const date = dayjs(value ?? undefined);
  return date.isValid() ? date.toISOString() : dayjs().toISOString();
};

const normalizeHistoryRange = (start: string | Date, end: string | Date): { start: string; end: string } => {
  let startIso = toIsoString(start);
  let endIso = toIsoString(end);
  if (dayjs(startIso).isAfter(endIso)) {
    [startIso, endIso] = [endIso, startIso];
  }
  return { start: startIso, end: endIso };
};

const createDefaultHistoryFilters = (): IrrigationPlanHistoryFilters => {
  const end = dayjs();
  const start = end.clone().subtract(1, "day");
  const range = normalizeHistoryRange(start.toISOString(), end.toISOString());
  return {
    scope: "project",
    projectId: null,
    planId: null,
    startDate: range.start,
    endDate: range.end,
  };
};

const buildHistoryIdentity = (filters: IrrigationPlanHistoryFilters) => {
  const entityId = filters.scope === "plan" ? (filters.planId ?? "") : (filters.projectId ?? "");
  return `${filters.scope}|${entityId}|${filters.startDate}|${filters.endDate}`;
};

const isHistorySelectionReady = (filters: IrrigationPlanHistoryFilters) =>
  filters.scope === "plan" ? Boolean(filters.planId) : Boolean(filters.projectId);

const extractHistoryRecords = (payload: unknown): IrrigationPlanState[] => {
  if (Array.isArray(payload)) {
    return payload as IrrigationPlanState[];
  }
  if (payload && typeof payload === "object" && Array.isArray((payload as { data?: unknown }).data)) {
    return (payload as { data: IrrigationPlanState[] }).data;
  }
  return [];
};

const normalizeStepIds = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  return value.map((stepId) => String(stepId));
};

const irrigationPlanHistoryFiltersBaseAtom = atom<IrrigationPlanHistoryFilters>(createDefaultHistoryFilters());
irrigationPlanHistoryFiltersBaseAtom.debugLabel = "irrigationPlanHistoryFiltersBaseAtom";

const irrigationPlanHistoryPaginationBaseAtom = atom<IrrigationPlanHistoryPagination>({
  limit: IRRIGATION_PLAN_HISTORY_PAGE_SIZE,
  offset: 0,
});
irrigationPlanHistoryPaginationBaseAtom.debugLabel = "irrigationPlanHistoryPaginationBaseAtom";

export const irrigationPlanHistoryEntriesAtom = atom<IrrigationPlanState[]>([]);
irrigationPlanHistoryEntriesAtom.debugLabel = "irrigationPlanHistoryEntriesAtom";

export const irrigationPlanHistoryLoadingAtom = atom<boolean>(false);
irrigationPlanHistoryLoadingAtom.debugLabel = "irrigationPlanHistoryLoadingAtom";

export const irrigationPlanHistoryErrorAtom = atom<string | null>(null);
irrigationPlanHistoryErrorAtom.debugLabel = "irrigationPlanHistoryErrorAtom";

export const irrigationPlanHistoryHasMoreAtom = atom<boolean>(false);
irrigationPlanHistoryHasMoreAtom.debugLabel = "irrigationPlanHistoryHasMoreAtom";

export const irrigationPlanHistoryFiltersAtom = atom(
  (get) => get(irrigationPlanHistoryFiltersBaseAtom),
  (
    get,
    set,
    update:
      | Partial<IrrigationPlanHistoryFilters>
      | ((prev: IrrigationPlanHistoryFilters) => IrrigationPlanHistoryFilters)
  ) => {
    const prev = get(irrigationPlanHistoryFiltersBaseAtom);
    let next = typeof update === "function" ? update(prev) : { ...prev, ...update };
    const range = normalizeHistoryRange(next.startDate, next.endDate);
    next = { ...next, startDate: range.start, endDate: range.end };

    const changed =
      prev.scope !== next.scope ||
      prev.projectId !== next.projectId ||
      prev.planId !== next.planId ||
      prev.startDate !== next.startDate ||
      prev.endDate !== next.endDate;

    set(irrigationPlanHistoryFiltersBaseAtom, next);

    if (changed) {
      set(irrigationPlanHistoryPaginationBaseAtom, {
        limit: IRRIGATION_PLAN_HISTORY_PAGE_SIZE,
        offset: 0,
      });
      set(irrigationPlanHistoryEntriesAtom, []);
      set(irrigationPlanHistoryHasMoreAtom, false);
      set(irrigationPlanHistoryErrorAtom, null);
      set(irrigationPlanHistoryLoadingAtom, false);
    }
  }
);
irrigationPlanHistoryFiltersAtom.debugLabel = "irrigationPlanHistoryFiltersAtom";

export const irrigationPlanHistoryPaginationAtom = atom(
  (get) => get(irrigationPlanHistoryPaginationBaseAtom),
  (
    get,
    set,
    update:
      | Partial<IrrigationPlanHistoryPagination>
      | ((prev: IrrigationPlanHistoryPagination) => IrrigationPlanHistoryPagination)
  ) => {
    const prev = get(irrigationPlanHistoryPaginationBaseAtom);
    const nextPartial = typeof update === "function" ? update(prev) : { ...prev, ...update };
    const limit = nextPartial.limit && nextPartial.limit > 0 ? nextPartial.limit : IRRIGATION_PLAN_HISTORY_PAGE_SIZE;
    const offset = Math.max(0, nextPartial.offset ?? prev.offset);
    const normalized = { limit, offset };

    const limitChanged = normalized.limit !== prev.limit;
    const offsetChanged = normalized.offset !== prev.offset;

    set(irrigationPlanHistoryPaginationBaseAtom, normalized);

    if (limitChanged || (offsetChanged && normalized.offset === 0)) {
      set(irrigationPlanHistoryEntriesAtom, []);
      set(irrigationPlanHistoryHasMoreAtom, false);
      set(irrigationPlanHistoryErrorAtom, null);
    }
  }
);
irrigationPlanHistoryPaginationAtom.debugLabel = "irrigationPlanHistoryPaginationAtom";

export const irrigationPlanHistorySelectionReadyAtom = atom((get) =>
  isHistorySelectionReady(get(irrigationPlanHistoryFiltersBaseAtom))
);
irrigationPlanHistorySelectionReadyAtom.debugLabel = "irrigationPlanHistorySelectionReadyAtom";

export const irrigationPlanHistoryIdentityAtom = atom((get) =>
  buildHistoryIdentity(get(irrigationPlanHistoryFiltersBaseAtom))
);
irrigationPlanHistoryIdentityAtom.debugLabel = "irrigationPlanHistoryIdentityAtom";

export const irrigationPlanHistoryRequestKeyAtom = atom((get) => {
  const identity = get(irrigationPlanHistoryIdentityAtom);
  const { limit, offset } = get(irrigationPlanHistoryPaginationBaseAtom);
  return `${identity}|${offset}|${limit}`;
});
irrigationPlanHistoryRequestKeyAtom.debugLabel = "irrigationPlanHistoryRequestKeyAtom";

export const fetchIrrigationPlanHistoryAtom = atom(null, async (get, set) => {
  const filters = get(irrigationPlanHistoryFiltersBaseAtom);
  const pagination = get(irrigationPlanHistoryPaginationBaseAtom);
  const requestKey = get(irrigationPlanHistoryRequestKeyAtom);

  if (!isHistorySelectionReady(filters)) {
    set(irrigationPlanHistoryEntriesAtom, []);
    set(irrigationPlanHistoryHasMoreAtom, false);
    set(irrigationPlanHistoryErrorAtom, null);
    set(irrigationPlanHistoryLoadingAtom, false);
    return;
  }

  set(irrigationPlanHistoryLoadingAtom, true);
  set(irrigationPlanHistoryErrorAtom, null);

  try {
    const response =
      filters.scope === "plan"
        ? await apiService.irrigationPlanState.readItemsByPlanId({
            irrigation_plan: filters.planId!,
            start_date: filters.startDate,
            end_date: filters.endDate,
            limit: pagination.limit,
            offset: pagination.offset,
            order: "desc",
          })
        : await apiService.irrigationPlanState.readItemsByProjectId({
            project: filters.projectId!,
            start_date: filters.startDate,
            end_date: filters.endDate,
            limit: pagination.limit,
            offset: pagination.offset,
            order: "desc",
          });

    const records = extractHistoryRecords(response);
    // console.log("[fetchIrrigationPlanHistory] Fetched records:", records.length);

    const latestKey = get(irrigationPlanHistoryRequestKeyAtom);
    if (latestKey !== requestKey) {
      console.log("[fetchIrrigationPlanHistory] Request key mismatch, discarding data", {
        requestKey,
        latestKey,
        recordsDiscarded: records.length,
      });
      return;
    }

    const append = pagination.offset > 0;
    const previous = get(irrigationPlanHistoryEntriesAtom);
    const previousIds = append ? new Set(previous.map((item) => item.id)) : new Set<number>();
    const merged = append ? [...previous, ...records.filter((item) => !previousIds.has(item.id))] : records;

    // console.log("[fetchIrrigationPlanHistory] Setting entries:", {
    //   append,
    //   previousCount: previous.length,
    //   newRecordsCount: records.length,
    //   mergedCount: merged.length,
    // });
    set(irrigationPlanHistoryEntriesAtom, merged);
    set(irrigationPlanHistoryHasMoreAtom, records.length === pagination.limit);
  } catch (error) {
    const message = error instanceof Error ? error.message : "Erro ao carregar histórico de planos de irrigação.";
    set(irrigationPlanHistoryErrorAtom, message);
    set(irrigationPlanHistoryHasMoreAtom, false);
    console.error("Failed to fetch irrigation plan history:", error);
  } finally {
    const latestKey = get(irrigationPlanHistoryRequestKeyAtom);
    if (latestKey === requestKey) {
      set(irrigationPlanHistoryLoadingAtom, false);
      if (!get(irrigationPlanHistoryEntriesAtom).length) {
        set(irrigationPlanHistoryHasMoreAtom, false);
      }
    }
  }
});
fetchIrrigationPlanHistoryAtom.debugLabel = "fetchIrrigationPlanHistoryAtom";

export const irrigationPlanHistoryTimelineAtom = atom<IrrigationPlanHistoryTimelineEntry[]>((get) => {
  const entries = get(irrigationPlanHistoryEntriesAtom);
  const irrigationPlans = get(irrigationPlansAtom);
  const projects = get(projectsAtom);

  const mapped: IrrigationPlanHistoryTimelineEntry[] = [];

  for (const entry of entries) {
    try {
      const planId = String(entry.irrigation_plan ?? "");
      const plan = planId ? irrigationPlans.find((p) => p.id === planId) : null;
      const projectId = plan?.project ?? null;
      const project = projectId ? projects.find((p) => p.id === projectId) : null;
      const sectors = project?.sectors?.toSorted((a, b) => a.name.localeCompare(b.name)) ?? [];
      const stepMap = plan?.steps
        ? new Map(plan.steps.map((step) => [step.id, step]))
        : new Map<string, AUTIrrigationPlan["steps"][number]>();
      const sectorNameById = new Map(sectors.map((sector) => [sector.id, sector.name ?? null]));

      const mapStepDetails = (stepIds: unknown): IrrigationPlanHistoryStepInfo[] =>
        normalizeStepIds(stepIds).map((stepId) => {
          const step = stepMap.get(stepId);
          const sectorId = step?.sector ?? null;
          const sectorName = sectorId ? (sectorNameById.get(sectorId) ?? null) : null;
          const details: IrrigationPlanHistoryStepInfo = {
            id: stepId,
            order: step?.order ?? null,
            description: step?.description ?? null,
            sectorId,
            sectorName,
          };
          return details;
        });

      const startTime = entry.start_time ?? null;
      const endTime = entry.end_time ?? null;
      const start = startTime ? dayjs(startTime) : null;
      const end = endTime ? dayjs(endTime) : null;
      const durationMsRaw = start ? (end ? end.diff(start) : dayjs().diff(start)) : 0;
      const durationMs = Number.isFinite(durationMsRaw) ? Math.max(0, durationMsRaw) : 0;

      const timelineEntry: IrrigationPlanHistoryTimelineEntry = {
        id: entry.id,
        irrigationPlanId: planId,
        planName: plan?.name ?? null,
        projectId,
        projectName: project?.name ?? null,
        startTime,
        endTime,
        endTimeInferred: entry.end_time_inferred,
        durationMs,
        durationSeconds: Math.max(0, Math.floor(durationMs / 1000)),
        activatedSteps: mapStepDetails(entry.activated_steps),
        activatedFertiSteps: mapStepDetails(entry.activated_ferti_steps),
        usesWaterPump: entry.uses_waterpump,
        usesFerti: entry.uses_ferti,
        waterpumpWorking: entry.waterpump_working,
        packetDate: entry.packet_date,
        isOngoing: !entry.end_time,
      };

      mapped.push(timelineEntry);
    } catch (error) {
      console.error(
        "[irrigationPlanHistoryTimelineAtom] Failed to transform entry",
        {
          entryId: entry?.id,
          irrigationPlan: entry?.irrigation_plan,
        },
        error
      );
    }
  }

  const buildSortKey = (record: IrrigationPlanHistoryTimelineEntry) =>
    record.startTime ?? record.endTime ?? record.packetDate ?? "";

  const sorted = [...mapped].sort((a, b) => {
    const aKey = buildSortKey(a);
    const bKey = buildSortKey(b);
    if (aKey === bKey) {
      return b.id - a.id;
    }
    return bKey.localeCompare(aKey);
  });
  return sorted;
});
irrigationPlanHistoryTimelineAtom.debugLabel = "irrigationPlanHistoryTimelineAtom";

export const irrigationPlanHistorySummaryAtom = atom<IrrigationPlanHistorySummary>((get) => {
  const entries = get(irrigationPlanHistoryTimelineAtom);
  if (entries.length === 0) {
    return {
      totalEntries: 0,
      ongoingEntries: 0,
      usesWaterPumpCount: 0,
      usesFertiCount: 0,
      lastEntry: null,
    };
  }

  let usesWaterPumpCount = 0;
  let usesFertiCount = 0;
  let ongoingEntries = 0;

  for (const entry of entries) {
    if (entry.usesWaterPump) usesWaterPumpCount += 1;
    if (entry.usesFerti) usesFertiCount += 1;
    if (entry.isOngoing) ongoingEntries += 1;
  }

  return {
    totalEntries: entries.length,
    ongoingEntries,
    usesWaterPumpCount,
    usesFertiCount,
    lastEntry: entries[0],
  };
});
irrigationPlanHistorySummaryAtom.debugLabel = "irrigationPlanHistorySummaryAtom";
